import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Report, User, BusinessType } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import { useTranslation } from '@/contexts/TranslationContext';
import { supabase } from '@/lib/supabase';


interface ReportFormProps {
  open: boolean;
  onClose: () => void;
  report?: Report;
}

const PRODUCTS = [
  'Βασιλική',
  'Μακεδονικός',
  'Ζωηφόρος',
  'Φλόγοινος',
  'Moschato Bello',
  'Τσίπουρο',
  'Ασκός'
];

const ReportForm: React.FC<ReportFormProps> = ({ open, onClose, report }) => {
  const { currentUser, customers, saveReport } = useAppContext();
  const { t } = useTranslation();
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [formData, setFormData] = useState({
    customerId: '',
    director: '',
    product: [] as string[],
    phone: '',
    competitor: '',
    goal: '',
    businessType: '',
    date: '',
    time: '',
    salesmanId: currentUser?.id || ''
  });

  useEffect(() => {
    fetchBusinessTypes();
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser]);

  const fetchBusinessTypes = async () => {
    const { data } = await supabase.from('business_types').select('*').order('name');
    if (data) setBusinessTypes(data);
  };

  const fetchUsers = async () => {
    const { data } = await supabase.from('users').select('*').order('name');
    if (data) setUsers(data);
  };

  useEffect(() => {
    if (report) {
      setFormData({
        customerId: report.customerId,
        director: report.director,
        product: Array.isArray(report.product) ? report.product : [report.product],
        phone: report.phone,
        competitor: report.competitor,
        goal: report.goal,
        businessType: report.businessType || '',
        date: report.date || '',
        time: report.time || '',
        salesmanId: report.salesmanId
      });
    } else {
      const now = new Date();
      const currentDate = now.toISOString().split('T')[0];
      const currentTime = now.toTimeString().split(' ')[0].slice(0, 5);
      
      setFormData({
        customerId: '',
        director: '',
        product: [],
        phone: '',
        competitor: '',
        goal: '',
        businessType: '',
        date: currentDate,
        time: currentTime,
        salesmanId: currentUser?.id || ''
      });
    }
  }, [report, currentUser]);


  const handleProductChange = (product: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      product: checked 
        ? [...prev.product, product]
        : prev.product.filter(p => p !== product)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.customerId) {
      alert('Please select a customer');
      return;
    }
    if (!formData.salesmanId) {
      alert('Salesman ID is missing. Please log in again.');
      return;
    }
    
    const success = await saveReport(formData);
    if (success) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{report ? t('editReport') : t('addReport')}</DialogTitle>
          <DialogDescription>
            {report ? t('updateReportDetails') : t('createNewReport')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {currentUser?.role === 'admin' && (
            <div className="space-y-2">
              <Label htmlFor="user">Assign to Salesperson</Label>
              <Select value={formData.salesmanId} onValueChange={(value) => setFormData({ ...formData, salesmanId: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select Salesperson" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          
          <div className="space-y-2">
            <Label htmlFor="customer">{t('businessName')}</Label>
            <Select value={formData.customerId} onValueChange={(value) => setFormData({ ...formData, customerId: value })}>
              <SelectTrigger>
                <SelectValue placeholder={t('selectCustomer')} />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.businessName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="businessType">Business Type</Label>
            <Select value={formData.businessType} onValueChange={(value) => setFormData({ ...formData, businessType: value })} required>
              <SelectTrigger>
                <SelectValue placeholder="Select Business Type" />
              </SelectTrigger>
              <SelectContent>
                {businessTypes.map((type) => (
                  <SelectItem key={type.id} value={type.name}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="time">Time</Label>
              <Input
                id="time"
                type="time"
                value={formData.time}
                onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="director">{t('director')}</Label>
              <Input
                id="director"
                value={formData.director}
                onChange={(e) => setFormData({ ...formData, director: e.target.value })}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone">{t('phone')}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
              />
            </div>
          </div>

          
          <div className="space-y-2">
            <Label>Products</Label>
            <div className="grid grid-cols-2 gap-2">
              {PRODUCTS.map((product) => (
                <div key={product} className="flex items-center space-x-2">
                  <Checkbox
                    id={product}
                    checked={formData.product.includes(product)}
                    onCheckedChange={(checked) => handleProductChange(product, checked as boolean)}
                  />
                  <Label htmlFor={product} className="text-sm">{product}</Label>
                </div>
              ))}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="competitor">{t('competitor')}</Label>
            <Input
              id="competitor"
              value={formData.competitor}
              onChange={(e) => setFormData({ ...formData, competitor: e.target.value })}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="goal">{t('goal')}</Label>
            <Textarea
              id="goal"
              value={formData.goal}
              onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
              rows={3}
              required
            />
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('cancel')}
            </Button>
            <Button type="submit">
              {report ? t('update') : t('add')} {t('report')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportForm;